import React, {useMemo} from 'react'
import {Box, Flex, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

import CTA from '../cms/cta'
import Copy from '../cms/copy'
import Fade from '../cms/fade'
import Heading from '../cms/heading'
import Icon from '../cms/icon'
import Slider from '../cms/slider'
import Teaser from '../cms/teaser'
import ModuleContainer from '../module-container'
import {MODULE_TYPE_LABELS, MODULE_TYPES} from '../../modules/constants'
import {hasValidAsset, hasValidString, isVideoAsset, parseAspectRatio} from '../../utils/utils'
import {ugcCarouselRecipe} from './index.recipe'

const CTA_VARIANT_MAP = {secondary: 'secondaryGhost'}

/**
 * Renders a reusable reel of portrait user-generated images and videos.
 *
 * @param {Object} props - Carousel properties.
 * @returns {JSX.Element|null} The UGC carousel or null when no valid tiles exist.
 */
const UgcCarousel = React.forwardRef(function UgcCarousel(
    {
        moduleId,
        moduleType = 'ugc-carousel',
        pageId,
        headingLevel = 2,
        alignment = 'left',
        aspectRatio = '9/16',
        heading = {},
        copy,
        cta,
        tiles = [],
        onIndexChange,
        anchor,
        background,
        spacing
    },
    ref
) {
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: ugcCarouselRecipe})
    const styles = useMemo(() => recipe({alignment}), [recipe, alignment])
    const validTiles = useMemo(
        () => (Array.isArray(tiles) ? tiles.filter((tile) => hasValidAsset(tile?.asset)) : []),
        [tiles]
    )
    const firstVideoIndex = useMemo(
        () => validTiles.findIndex((tile) => isVideoAsset(tile.asset)),
        [validTiles]
    )
    const ariaLabel = useMemo(
        () =>
            [
                hasValidString(heading.subline) && `${heading.subline} -`,
                heading.headline1,
                heading.headline2
            ]
                .filter(hasValidString)
                .join(' '),
        [heading]
    )

    if (!validTiles.length) return null

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background={background}
            spacing={spacing}
            ref={ref}
            {...styles.moduleContainer}
        >
            <Flex {...styles.headerContainer}>
                <Flex {...styles.headingCopyContainer}>
                    <Fade>
                        <Heading
                            {...heading}
                            headingLevel={headingLevel}
                            alignment={alignment}
                            type={formatMessage(MODULE_TYPE_LABELS[MODULE_TYPES.CAROUSEL])}
                        />
                    </Fade>
                    {hasValidString(copy?.text) && (
                        <Fade>
                            <Copy {...copy} alignment={alignment} />
                        </Fade>
                    )}
                </Flex>
                {hasValidString(cta?.label) && (
                    <Box {...styles.ctaContainer}>
                        <CTA
                            {...cta}
                            variant={CTA_VARIANT_MAP[cta.variant] || cta.variant}
                            trackingContext={{moduleName: moduleType, eventLabel: 'cta'}}
                        />
                    </Box>
                )}
            </Flex>

            <Box {...styles.sliderContainer}>
                <Slider
                    sliderLabel={ariaLabel}
                    variant="carousel"
                    itemsPerSlide={{base: 1.1, s: 3}}
                    onIndexChange={onIndexChange}
                >
                    {validTiles.map((tile, index) => {
                        const asset = isVideoAsset(tile.asset)
                            ? {
                                  ...tile.asset,
                                  autoplay: index === firstVideoIndex,
                                  loop: true,
                                  videoType: 'animation',
                                  videoControls: false
                              }
                            : tile.asset

                        return (
                            <Box key={tile.id || index} {...styles.tile}>
                                <Teaser
                                    asset={asset}
                                    headline=""
                                    copy={{text: ''}}
                                    teaserBackground={false}
                                    aspectRatio={parseAspectRatio(aspectRatio)}
                                    widths={{base: 360, s: 400, m: 480, l: 640}}
                                />
                                {hasValidString(tile.tag?.label) && (
                                    <Box {...styles.tag}>
                                        {tile.tag.icon && <Icon {...tile.tag.icon} />}
                                        <Box as="span">{tile.tag.label}</Box>
                                    </Box>
                                )}
                            </Box>
                        )
                    })}
                </Slider>
            </Box>
        </ModuleContainer>
    )
})

UgcCarousel.propTypes = {
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    headingLevel: PropTypes.number,
    alignment: PropTypes.oneOf(['left', 'center']),
    aspectRatio: PropTypes.string,
    heading: PropTypes.object,
    copy: PropTypes.object,
    cta: PropTypes.object,
    tiles: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            asset: PropTypes.object,
            tag: PropTypes.shape({
                label: PropTypes.string,
                icon: PropTypes.shape({
                    name: PropTypes.string,
                    title: PropTypes.string,
                    alt: PropTypes.string
                })
            })
        })
    ),
    onIndexChange: PropTypes.func,
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom'])
}

export default UgcCarousel
